/* Stacks — quote tool.
   Several STL / OBJ / 3MF files per quote. Each file is parsed in the browser, previewed in 3D,
   checked for printability (build volume, open mesh, tiny/thin parts) and priced on the
   "print service" product: every variant costs exactly one unit of currency, so the cart
   quantity *is* the price (per piece × pieces). The price itself comes from print-math.js —
   weight from the shell and infill, print time from the slicer model, plus finish and
   support fees — and the order guard re-runs the same file on the real upload.
   Every file becomes its own cart line with the model attached as a line-item property.
   `?edit=<uploaded file id>` reopens a print from the cart (line keys change whenever the cart does): the model is fetched back from
   Shopify's CDN, specs are restored, and saving replaces the old line. */
import { parseMaterials, parseSettings, quantityFor, normaliseOptions, packOptions, unpackOptions, TIERS } from 'print-math';

/* three.js is only needed once there is a file to look at — roughly 600 KB that the drop
   screen shouldn't wait for. It starts downloading the moment the visitor reaches for the
   picker, so by the time a file is parsed it is usually already here. */
let THREE, OrbitControls, STLLoader, OBJLoader, ThreeMFLoader;
let threePromise;
const loadThree = () => (threePromise ||= Promise.all([
  import('three'),
  import('three/addons/controls/OrbitControls.js'),
  import('three/addons/loaders/STLLoader.js'),
  import('three/addons/loaders/OBJLoader.js'),
  import('three/addons/loaders/3MFLoader.js'),
]).then(([three, oc, stl, obj, mf]) => {
  THREE = three; OrbitControls = oc.OrbitControls; STLLoader = stl.STLLoader; OBJLoader = obj.OBJLoader; ThreeMFLoader = mf.ThreeMFLoader;
}));

const form = document.getElementById('quote');
const cfg = JSON.parse(document.getElementById('quote-config').textContent);
const $ = (s, r = form) => r.querySelector(s);
const $$ = (s, r = form) => [...r.querySelectorAll(s)];
const fmt = (c) => window.formatMoney ? formatMoney(c) : (c / 100).toFixed(2);
const S = window.Stacks || {};
/* MonoApp → Instant quote switched off: people can still measure a file, but nothing is sold */
if (cfg.appEnabled === false) {
  const off = document.querySelector('[data-submit]');
  if (off) { off.disabled = true; off.title = 'Ordering is paused'; }
}

// Refresh entitlements and rates before computing any quote, including an old
// Liquid config saved before an app upgrade. A failed config load pauses orders.
if (cfg.signUrl) {
  try {
    const r=await fetch(cfg.signUrl.replace(/\/sign$/, '/config'),{cache:'no-store'});
    if (!r.ok) throw new Error('Quote configuration unavailable');
    const live=await r.json();
    Object.assign(cfg,{materials:live.materials,pricing:live.pricing,appEnabled:live.enabled});
  } catch {cfg.appEnabled=false;}
}
const materials = parseMaterials(cfg.materials);
$$('input[name="_material"]').forEach(i=>{if (!materials[i.value.toLowerCase()]) {i.disabled=true;i.closest('label')?.setAttribute('hidden','');}});
const pricing = parseSettings(cfg.pricing);
const CENT = 100;                                              // the variant's price, in cents
const tierIdOf = (val) => $$('input[name="_tier"]').find(i => i.value === val)?.dataset.tierId || 'standard';
const fmtHours = (h) => h < 1 ? `${Math.max(1, Math.round(h * 60))} min` : `${h.toFixed(1)} ${cfg.t.hours}`;

/** @type {Array<{uid:string,file:File,name:string,geos:THREE.BufferGeometry[],vol:number,size:THREE.Vector3,area:number,openEdges:number,scale:number,material:string,tier:string,opts:object,color:string,pieces:number,note:string,sig?:string}>} */
const items = [];
let active = -1;
let editKey = null;
let submitting = false;

const defaults = () => {
  const tier = $('input[name="_tier"]:checked')?.value || cfg.variants[0]?.o2;
  return {
    material: $('input[name="_material"]:checked')?.value || cfg.variants[0]?.o1,
    tier, opts: { ...TIERS[tierIdOf(tier)] || TIERS.standard },
    color: $('input[name="_color"]:checked')?.value || '',
    pieces: 1, note: ''
  };
};

/* ================================================================ viewer */
const viewerEl = $('[data-viewer]');
let renderer, scene, camera, controls, meshGroup;

function initViewer() {
  if (renderer) return;
  renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
  renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
  viewerEl.prepend(renderer.domElement);
  scene = new THREE.Scene();
  camera = new THREE.PerspectiveCamera(40, 1, 0.1, 100000);
  controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = .08;
  controls.rotateSpeed = .85;
  controls.zoomSpeed = .9;
  controls.enablePan = false;                          // nothing to pan to; it only ever loses the model
  controls.enableZoom = true;                          // two-finger pinch on touch
  // A plain wheel belongs to the page, so keep it away from the controls; Ctrl/⌘ + wheel zooms.
  renderer.domElement.addEventListener('wheel', (e) => {
    if (e.ctrlKey || e.metaKey) { e.preventDefault(); zoomBy(e.deltaY > 0 ? 1.12 : 0.89); }
    e.stopPropagation();
  }, { capture: true, passive: false });
  renderer.domElement.addEventListener('pointerdown', () => {
    controls.autoRotate = false;
    viewerEl.classList.add('is-turning');
    hideHint();
  });
  addEventListener('pointerup', () => viewerEl.classList.remove('is-turning'));
  renderer.domElement.addEventListener('dblclick', frameView);
  controls.autoRotate = true;
  controls.autoRotateSpeed = 1.2;
  scene.add(new THREE.HemisphereLight(0xffffff, 0x3a3d44, 2.4));
  const key = new THREE.DirectionalLight(0xffffff, 2.6); key.position.set(1, 2, 1.5); scene.add(key);
  const fill = new THREE.DirectionalLight(0xffffff, 1.2); fill.position.set(-1.5, .5, 1); scene.add(fill);
  const rim = new THREE.DirectionalLight(0xd2f24b, 1.6); rim.position.set(-2, 1, -2); scene.add(rim);
  const resize = () => {
    const { width, height } = viewerEl.getBoundingClientRect();
    if (!width || !height) return;            // still hidden — measuring now would zero the buffer
    renderer.setSize(width, height, false);
    camera.aspect = width / height; camera.updateProjectionMatrix();
  };
  new ResizeObserver(resize).observe(viewerEl);
  resize();
  let visible = true;
  new IntersectionObserver(([e]) => { visible = e.isIntersecting; }).observe(viewerEl);
  const loop = () => { if (visible) { controls.update(); renderer.render(scene, camera); } requestAnimationFrame(loop); };
  loop();
}

/* Zoom is the camera walking along its own sight line. Clamped either side so no amount of
   clicking can push the model out of the frame or turn it inside out. */
function zoomBy(factor) {
  if (!camera || !controls) return;
  const dir = camera.position.clone().sub(controls.target);
  const span = new THREE.Box3().setFromObject(meshGroup || scene).getSize(new THREE.Vector3()).length() || 100;
  const next = Math.min(span * 6, Math.max(span * 0.35, dir.length() * factor));
  camera.position.copy(controls.target).add(dir.setLength(next));
  controls.autoRotate = false;
}

const hexFor = (colorName) => $$('input[name="_color"]').find(i => i.value === colorName)?.dataset.hex || '#f2f0e9';
/* The preview lifts very dark colours a little: a true #16171A part on the dark stage is a
   silhouette, and the shopper needs to see edges and layer lines. The swatch, the cart
   and the order keep the real name and hex. */
const previewHex = (hex) => {
  const n = parseInt(String(hex).replace('#', ''), 16); if (Number.isNaN(n)) return hex;
  const r = n >> 16 & 255, g = n >> 8 & 255, b = n & 255;
  const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  if (lum > 0.18) return hex;
  const lift = (c) => Math.min(255, Math.round(c + (70 - c) * 0.7));
  return '#' + [lift(r), lift(g), lift(b)].map((c) => c.toString(16).padStart(2, '0')).join('');
};

/* Everyday objects at their real millimetre sizes. Nothing explains "126 mm" like a bank
   card standing next to it. */
const REFS = {
  card:   { l: 85.6, w: 54, h: 0.8, shape: 'box' },
  phone:  { l: 147, w: 71, h: 8, shape: 'box' },
  can:    { r: 33, h: 123, shape: 'cyl' },
  mug:    { r: 40, h: 95, shape: 'cyl' },
  a4:     { l: 297, w: 210, h: 0.2, shape: 'box' },
  person: { r: 170, h: 1700, shape: 'person' }
};
let refGroup = null, compareKey = '';

function buildRef(key) {
  const spec = REFS[key];
  if (!spec) return null;
  const g = new THREE.Group();
  // barely there on purpose: the print is the subject, this is only a yardstick
  const skin = new THREE.MeshStandardMaterial({ color: 0xF2F0E9, roughness: .9, transparent: true, opacity: .1, depthWrite: false });
  const line = new THREE.LineBasicMaterial({ color: 0xD2F24B, transparent: true, opacity: .4 });
  const add = (geo, y) => {
    const m = new THREE.Mesh(geo, skin); m.position.y = y; g.add(m);
    const e = new THREE.LineSegments(new THREE.EdgesGeometry(geo, 25), line); e.position.y = y; g.add(e);
  };
  if (spec.shape === 'box') add(new THREE.BoxGeometry(spec.l, spec.h, spec.w), spec.h / 2);
  else if (spec.shape === 'cyl') add(new THREE.CylinderGeometry(spec.r, spec.r, spec.h, 40), spec.h / 2);
  else {
    const H = spec.h;                                   // 1700 mm, roughly head height
    add(new THREE.SphereGeometry(105, 20, 14), H - 105);                        // head
    add(new THREE.BoxGeometry(380, H * .3, 180), H * .66);                      // torso
    [-95, 95].forEach(x => {                                                    // legs
      const leg = new THREE.CylinderGeometry(65, 55, H * .5, 14);
      const m = new THREE.Mesh(leg, skin); m.position.set(x, H * .25, 0); g.add(m);
      g.add(Object.assign(new THREE.LineSegments(new THREE.EdgesGeometry(leg, 25), line), { position: m.position.clone() }));
    });
    [-240, 240].forEach(x => {                                                  // arms
      const arm = new THREE.CylinderGeometry(45, 40, H * .32, 12);
      const m = new THREE.Mesh(arm, skin); m.position.set(x, H * .63, 0); g.add(m);
      g.add(Object.assign(new THREE.LineSegments(new THREE.EdgesGeometry(arm, 25), line), { position: m.position.clone() }));
    });
  }
  return g;
}

function placeRef() {
  if (refGroup) { scene.remove(refGroup); refGroup = null; }
  if (!compareKey || !meshGroup) return;
  refGroup = buildRef(compareKey);
  if (!refGroup) return;
  const model = new THREE.Box3().setFromObject(meshGroup).getSize(new THREE.Vector3());
  const ref = new THREE.Box3().setFromObject(refGroup).getSize(new THREE.Vector3());
  refGroup.position.x = model.x / 2 + ref.x / 2 + Math.max(12, model.x * .12);   // side by side, not overlapping
  scene.add(refGroup);
}

/* A painted blob rather than a real shadow map: the same "it is standing on something" cue
   for none of the cost. */
let shadowTex = null;
function contactShadow() {
  if (!shadowTex) {
    const c = document.createElement('canvas');
    c.width = c.height = 128;
    const ctx = c.getContext('2d');
    const g = ctx.createRadialGradient(64, 64, 2, 64, 64, 62);
    g.addColorStop(0, 'rgba(0,0,0,.55)');
    g.addColorStop(.55, 'rgba(0,0,0,.22)');
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 128, 128);
    shadowTex = new THREE.CanvasTexture(c);
  }
  const m = new THREE.Mesh(
    new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2),
    new THREE.MeshBasicMaterial({ map: shadowTex, transparent: true, depthWrite: false })
  );
  m.name = 'shadow';
  return m;
}

function buildPlate() {
  const bed = cfg.bed;
  const g = new THREE.Group();
  g.name = 'plate';
  const grid = new THREE.GridHelper(bed, Math.max(4, Math.round(bed / 10)), 0x50555F, 0x2C3036);
  grid.material.transparent = true; grid.material.opacity = .5;
  g.add(grid);
  const rim = new THREE.LineSegments(
    new THREE.EdgesGeometry(new THREE.PlaneGeometry(bed, bed).rotateX(-Math.PI / 2)),
    new THREE.LineBasicMaterial({ color: 0xD2F24B, transparent: true, opacity: .45 })
  );
  g.add(rim);
  return g;
}

function showModel(item) {
  initViewer();
  if (meshGroup) scene.remove(meshGroup);
  meshGroup = new THREE.Group();
  const mat = new THREE.MeshStandardMaterial({ color: previewHex(hexFor(item.color)), roughness: .5, metalness: .05 });
  item.geos.forEach(g => meshGroup.add(new THREE.Mesh(g, mat)));
  meshGroup.rotation.x = -Math.PI / 2;                 // Z-up files → Y-up scene
  const sc = scaleOf(item);
  meshGroup.scale.set(sc.x, sc.y, sc.z);               // local axes are still the file's, the rotation comes after
  scene.add(meshGroup);

  const box = new THREE.Box3().setFromObject(meshGroup);
  const center = box.getCenter(new THREE.Vector3());
  meshGroup.position.x -= center.x;
  meshGroup.position.z -= center.z;
  meshGroup.position.y -= box.min.y;                   // stand it on the plate

  if (!scene.getObjectByName('plate')) scene.add(buildPlate());

  const worn = scene.getObjectByName('shadow');
  if (worn) scene.remove(worn);
  const foot = new THREE.Box3().setFromObject(meshGroup).getSize(new THREE.Vector3());
  const blob = contactShadow();
  blob.scale.set(Math.max(foot.x, 1) * 1.7, 1, Math.max(foot.z, 1) * 1.7);
  blob.position.y = 0.25;                              // a hair above the plate, to avoid z-fighting
  scene.add(blob);

  placeRef();
  frameView();
  $('[data-viewer-empty]').hidden = true;
  $$('[data-hud]').forEach(el => { el.hidden = false; });
  showHint();
}

/* The print is the subject. The comparison object shares the frame, but a 250 mm plate is
   only pulled in when it would not shrink a small print to a dot — it still shows, just
   running past the edges, which reads as "small thing on a big bed" anyway. */
function frameView() {
  if (!camera || !meshGroup) return;
  const box = new THREE.Box3().setFromObject(meshGroup);
  if (box.isEmpty()) return;
  if (refGroup) box.expandByObject(refGroup);
  const plate = scene.getObjectByName('plate');
  const span = box.getSize(new THREE.Vector3());
  if (plate && cfg.bed <= Math.max(span.x, span.y, span.z) * 2.5) box.expandByObject(plate);
  const size = box.getSize(new THREE.Vector3());
  const mid = box.getCenter(new THREE.Vector3());
  const dist = Math.max(size.x, size.y, size.z) * 1.75;   // a little air around the part in the 4:3 frame
  const lift = size.y > size.x * 2 ? .35 : .7;          // tall scenes read better from lower down
  camera.position.set(mid.x + dist, mid.y + dist * lift, mid.z + dist);
  controls.target.copy(mid);
  camera.near = Math.max(0.1, dist / 1000); camera.far = dist * 50; camera.updateProjectionMatrix();
  controls.autoRotate = true;
}

function clearViewer() {
  if (meshGroup) { scene.remove(meshGroup); meshGroup = null; }
  $('[data-viewer-empty]').hidden = false;
  $$('[data-hud]').forEach(el => { el.hidden = true; });
}

/* ================================================================ geometry */
function analyse(geos) {
  let vol = 0, area = 0;
  const box = new THREE.Box3();
  const edges = new Map();
  const key = (x, y, z) => `${Math.round(x * 1e3)},${Math.round(y * 1e3)},${Math.round(z * 1e3)}`;
  const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3();
  for (const geo of geos) {
    const g = geo.index ? geo.toNonIndexed() : geo;
    const p = g.attributes.position.array;
    const heavy = p.length / 9 > 400000;                // skip edge map on huge meshes (memory)
    for (let i = 0; i < p.length; i += 9) {
      vol += p[i] * (p[i + 4] * p[i + 8] - p[i + 7] * p[i + 5])
           - p[i + 3] * (p[i + 1] * p[i + 8] - p[i + 7] * p[i + 2])
           + p[i + 6] * (p[i + 1] * p[i + 5] - p[i + 4] * p[i + 2]);
      a.set(p[i], p[i + 1], p[i + 2]); b.set(p[i + 3], p[i + 4], p[i + 5]); c.set(p[i + 6], p[i + 7], p[i + 8]);
      area += b.clone().sub(a).cross(c.clone().sub(a)).length() / 2;
      if (!heavy) {
        const ka = key(p[i], p[i + 1], p[i + 2]), kb = key(p[i + 3], p[i + 4], p[i + 5]), kc = key(p[i + 6], p[i + 7], p[i + 8]);
        for (const [u, v] of [[ka, kb], [kb, kc], [kc, ka]]) {
          const k = u < v ? u + '|' + v : v + '|' + u;
          edges.set(k, (edges.get(k) || 0) + 1);
        }
      }
    }
    geo.computeBoundingBox(); box.union(geo.boundingBox); geo.computeVertexNormals();
  }
  let open = 0;
  edges.forEach(n => { if (n !== 2) open++; });
  return { vol: Math.abs(vol / 6), area, size: box.getSize(new THREE.Vector3()), openEdges: open };
}

function collectGeometries(obj) {
  const out = [];
  obj.updateMatrixWorld(true);
  obj.traverse(c => { if (c.isMesh && c.geometry) { const g = c.geometry.clone(); g.applyMatrix4(c.matrixWorld); out.push(g); } });
  return out;
}

async function parse(file) {
  const ext = file.name.split('.').pop().toLowerCase();
  const buf = await file.arrayBuffer();
  if (ext === 'stl') return [new STLLoader().parse(buf)];
  if (ext === 'obj') return collectGeometries(new OBJLoader().parse(new TextDecoder().decode(buf)));
  if (ext === '3mf') return collectGeometries(new ThreeMFLoader().parse(buf));
  throw new Error('type');
}

/* ================================================================ size
   `unit` turns the file's numbers into millimetres, `f` is what the shopper dialled in.
   Everything downstream — preview, checks, price — reads the millimetres these produce. */
const AXES = ['x', 'y', 'z'];
const scaleOf = (it) => ({ x: it.unit * it.f.x, y: it.unit * it.f.y, z: it.unit * it.f.z });
const dimsOf = (it) => { const s = scaleOf(it); return { x: it.size.x * s.x, y: it.size.y * s.y, z: it.size.z * s.z }; };
const volFactor = (it) => { const s = scaleOf(it); return s.x * s.y * s.z; };
/* surface area in mm² after the resize. Uniform scaling is the common case and cheap; a
   per-axis stretch changes every triangle differently, so walk the mesh again (cached). */
function areaOf(it) {
  const s = scaleOf(it);
  if (Math.abs(s.x - s.y) < 1e-9 && Math.abs(s.x - s.z) < 1e-9) return it.area * s.x * s.x;
  const key = `${s.x},${s.y},${s.z}`;
  if (it.areaKey === key) return it.areaCache;
  let area = 0;
  const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3();
  for (const geo of it.geos) {
    const g = geo.index ? geo.toNonIndexed() : geo;
    const p = g.attributes.position.array;
    for (let i = 0; i < p.length; i += 9) {
      a.set(p[i] * s.x, p[i + 1] * s.y, p[i + 2] * s.z);
      b.set(p[i + 3] * s.x, p[i + 4] * s.y, p[i + 5] * s.z);
      c.set(p[i + 6] * s.x, p[i + 7] * s.y, p[i + 8] * s.z);
      area += b.sub(a).cross(c.sub(a)).length() / 2;
    }
  }
  it.areaKey = key; it.areaCache = area;
  return area;
}

/* ================================================================ checks + pricing */
function checks(it) {
  const d3 = dimsOf(it);
  const dims = [d3.x, d3.y, d3.z];
  const s = Math.cbrt(volFactor(it));
  const out = [];
  if (dims.some(d => d > cfg.bed)) out.push(['bad', cfg.t.chkBed]);
  if (Math.min(...dims) < 1.5) out.push(['warn', cfg.t.chkSmall]);
  const thickness = it.area > 0 ? (2 * it.vol * s) / it.area : 99;   // ≈ average wall thickness in mm
  if (thickness < 0.8) out.push(['warn', cfg.t.chkThin]);
  if (it.openEdges > 0) out.push(['warn', cfg.t.chkOpen]);
  if (!out.length) out.push(['ok', cfg.t.chkOk]);
  return out;
}
const level = (it) => { const c = checks(it); return c.some(x => x[0] === 'bad') ? 'bad' : c.some(x => x[0] === 'warn') ? 'warn' : 'ok'; };

const variantFor = (it) => cfg.variants.find(v => v.o1 === it.material && (v.o2 == null || v.o2 === it.tier));

function quoteFor(it) {
  const v = variantFor(it);
  if (!v) return null;
  const volMm3 = Math.round(it.vol * volFactor(it));
  const dm = dimsOf(it);
  const areaMm2 = Math.round(areaOf(it));
  const heightMm = Math.round(dm.z * 100) / 100;
  const q = quantityFor({
    volumeMm3: volMm3, areaMm2, heightMm, material: it.material, materials, settings: pricing,
    options: it.opts, pieces: it.pieces, unitPriceCents: v.price
  });
  if (!q) return null;
  return {
    v, q, qty: q.quantity, minApplied: q.isMinimumApplied, total: v.price * q.quantity,
    grams: Math.round(q.weight * 10) / 10, hours: q.printTimeHours, layers: q.numLayers,
    dims: `${dm.x.toFixed(0)} × ${dm.y.toFixed(0)} × ${dm.z.toFixed(0)} mm`,
    volMm3, areaMm2, heightMm, cm3: volMm3 / 1000
  };
}

/* ================================================================ render */
const esc = (t) => String(t).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

function renderList() {
  const box = $('[data-files]');
  box.innerHTML = items.map((it, i) => {
    const q = quoteFor(it);
    const lv = level(it);
    const icon = { ok: '✓', warn: '!', bad: '✕' }[lv];
    return `<div class="qfile${i === active ? ' is-active' : ''}" role="option" aria-selected="${i === active}" tabindex="0" data-pick="${i}">
      <span class="qfile__thumb">${esc(it.name.split('.').pop().toUpperCase())}</span>
      <span class="qfile__name"><b>${esc(it.name)}</b><small>${q ? esc(`${it.material} · ${it.tier} · ${it.opts.infill}% · ×${it.pieces}`) : ''}</small></span>
      <span class="qfile__price">${q ? fmt(q.total) : '—'}</span>
      <span style="display:flex;gap:4px;align-items:center"><span class="qfile__state is-${lv}" aria-hidden="true">${icon}</span>
      <button type="button" class="qfile__rm" data-remove="${i}" aria-label="${esc(cfg.t.remove)} ${esc(it.name)}"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6 6 18"/></svg></button></span>
    </div>`;
  }).join('');
  box.querySelector('.is-active')?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  $('[data-drop]').hidden = items.length > 0;
  $('[data-add-more]').hidden = items.length === 0 || items.length >= cfg.maxFiles || !!editKey;
  $('[data-filebar]').hidden = active < 0;
}

function syncSpecs() {
  const it = items[active];
  if (!it) return;
  const set = (name, val) => { const el = $$(`input[name="${name}"]`).find(i => i.value === val); if (el) el.checked = true; };
  set('_material', it.material); set('_tier', it.tier); set('_color', it.color);
  syncOptions(it);
  $('[data-pieces]').value = it.pieces;
  $('[data-note]').value = it.note;
  syncSize(it);
  $('[data-specs-for]').textContent = items.length > 1 ? it.name : '';
  $('[data-filename]').textContent = `${it.name} · ${(it.file.size / 1048576).toFixed(1)} MB`;
}

/* the advanced controls show the selected file's options; a tier is only "checked" while
   the options still match its preset, the way a slicer profile stops being one when edited */
function syncOptions(it) {
  const o = it.opts;
  const infill = $('[data-opt="infill"]');
  if (infill && document.activeElement !== infill) infill.value = o.infill;
  $('[data-infill-out]').textContent = `${o.infill}%`;
  $$('[data-opt="wallStrength"]').forEach(i => { i.checked = i.value === o.wallStrength; });
  $$('[data-opt="quality"]').forEach(i => { i.checked = i.value === o.quality; });
  $$('[data-opt="finish"]').forEach(i => { i.checked = i.value === o.finish; });
  const sup = $('[data-opt="supports"]'); if (sup) sup.checked = !!o.supports;
  const preset = TIERS[tierIdOf(it.tier)];
  const matches = preset && packOptions(normaliseOptions(preset)) === packOptions(o);
  $$('input[name="_tier"]').forEach(i => { i.checked = matches && i.value === it.tier; });
  $('[data-advanced]')?.classList.toggle('is-custom', !matches);
}

function syncSize(it) {
  const d = dimsOf(it);
  AXES.forEach(ax => {
    const el = $(`[data-dim="${ax}"]`);
    if (el && document.activeElement !== el) el.value = d[ax] < 100 ? d[ax].toFixed(1) : d[ax].toFixed(0);
  });
  const pct = $('[data-pct]');
  if (pct && document.activeElement !== pct) pct.value = Math.round(it.f.x * 100);
}

/* resize around the axis the shopper touched, keeping proportions unless they unlocked them */
function resizeTo(it, ax, mm) {
  const base = it.size[ax] * it.unit;
  if (!(base > 0) || !(mm > 0)) return;
  const next = mm / base;
  if ($('[data-lock]')?.checked) AXES.forEach(a => { it.f[a] = next; });
  else it.f[ax] = next;
}

function renderSummary() {
  const it = items[active];
  const q = it && quoteFor(it);
  $('[data-color-name]').textContent = it?.color || $('input[name="_color"]:checked')?.value || '';
  $('[data-o-dims]').textContent = q ? q.dims : '—';
  $('[data-o-grams]').textContent = q ? `${q.grams} g` : '—';
  $('[data-o-time]').textContent = q ? fmtHours(q.hours) : '—';
  $('[data-o-layers]').textContent = q ? q.layers : '—';
  $('[data-o-mat-label]').textContent = it ? `${it.material} · ${fmt(materials[it.material.toLowerCase()]?.pricePerGram * CENT || 0)}/g` : '';
  $('[data-o-mat]').textContent = q ? fmt(Math.round(q.q.materialCost * CENT)) : '—';
  $('[data-o-print]').textContent = q ? fmt(Math.round(q.q.printingCost * CENT)) : '—';
  $('[data-o-finish-row]').hidden = !(q && q.q.finishCost > 0);
  $('[data-o-finish]').textContent = q ? fmt(Math.round(q.q.finishCost * CENT)) : '—';
  $('[data-o-supports-row]').hidden = !(q && q.q.supportCost > 0);
  $('[data-o-supports]').textContent = q ? fmt(Math.round(q.q.supportCost * CENT)) : '—';
  const struck = (full, now) => (full > now ? ` <s>${esc(fmt(full))}</s>` : '');
  $('[data-o-piece]').innerHTML = q ? esc(fmt(q.q.itemTotal * CENT)) + struck(q.q.fullTotal * CENT, q.q.itemTotal * CENT) : '—'; const pc = $('[data-o-piece-copy]'); if (pc) pc.innerHTML = $('[data-o-piece]').innerHTML;
  $('[data-o-pieces]').textContent = it ? it.pieces : 1;
  $('[data-dims]').textContent = q ? q.dims : '';
  $('[data-vol]').textContent = q ? `${q.cm3.toFixed(1)} cm³` : '';
  $('[data-o-min]').hidden = !(q && q.minApplied);

  const quotes = items.map(quoteFor);
  const total = quotes.reduce((s, x) => s + (x ? x.total : 0), 0);
  /* on sale: what it would have cost, struck — per piece × pieces, like the total */
  const full = quotes.reduce((s, x) => s + (x ? (x.q.fullTotal || x.q.itemTotal) * CENT * x.q.pieces : 0), 0);
  $('[data-o-total]').innerHTML = items.length ? esc(fmt(total)) + struck(full, total) : '—';
  const sale = $('[data-o-sale]'), pct = quotes.find(Boolean)?.q.salePercent || 0;
  if (sale) { sale.hidden = !(items.length && pct > 0 && full > total); sale.textContent = (sale.dataset.t || '').replace('[pct]', pct); }
  $('[data-o-files-row]').hidden = items.length < 2;
  $('[data-o-files]').textContent = items.length;

  const list = it ? checks(it) : [];
  const box = $('[data-checks]');
  box.hidden = !it;
  box.innerHTML = list.map(([lv, msg]) => `<div class="qcheck__row ${lv}"><i>${{ ok: '✓', warn: '!', bad: '✕' }[lv]}</i><span>${esc(msg)}</span></div>`).join('')
    + (items.some(x => level(x) === 'bad') ? `<div class="qcheck__row bad"><i>✕</i><span>${esc(cfg.t.chkBlocked)}</span></div>` : '');

  const blocked = cfg.appEnabled === false || submitting || !items.length || items.some(x => level(x) === 'bad' || !quoteFor(x)?.v.available || quoteFor(x)?.v.price !== CENT);
  const btn = $('[data-submit]');
  btn.disabled = blocked;
  const lbl = $('[data-submit-label]');                // absent while the button shows its spinner
  if (lbl) lbl.textContent = editKey ? cfg.t.save : items.length > 1 ? cfg.t.addMany.replace('[count]', items.length) : cfg.t.addOne;
  if (meshGroup && it) meshGroup.children.forEach(m => m.material.color.set(previewHex(hexFor(it.color))));
}

/* empty state vs. workspace, and the phone price bar that mirrors the CTA */
function syncStage() {
  const on = items.length > 0;
  $('[data-empty]').hidden = on;
  $('[data-work]').hidden = !on;
  const bar = $('[data-mbar]');
  bar.hidden = !on;
  if (!on) return;
  const btn = $('[data-submit]'), mBtn = $('[data-m-submit]');
  $('[data-m-total]').innerHTML = $('[data-o-total]').innerHTML;
  mBtn.disabled = btn.disabled;
  const lbl = $('[data-m-label]');
  if (lbl) lbl.textContent = $('[data-submit-label]')?.textContent || '';
}

const render = () => { renderList(); renderSummary(); syncStage(); };

function select(i) {
  active = i;
  syncStage();                                // lay the workspace out before three.js measures the viewer
  if (items[i]) { syncSpecs(); showModel(items[i]); } else clearViewer();
  render();
}

/* ================================================================ files */
function showError(msg) {
  const e = items.length ? $('[data-error-work]') : $('[data-error]');
  $$('[data-error], [data-error-work]').forEach(x => { x.textContent = ''; x.hidden = true; });
  e.textContent = msg || '';
  e.hidden = !msg;
}

async function addFile(file, preset = {}) {
  if (!/\.(stl|obj|3mf)$/i.test(file.name)) throw new Error(cfg.t.badType);
  await loadThree();
  if (file.size > cfg.maxBytes) throw new Error(`${file.name}: ${cfg.t.tooBig}`);
  if (items.length >= cfg.maxFiles) throw new Error(cfg.t.tooMany);
  const geos = await parse(file).catch(() => { throw new Error(`${file.name}: ${cfg.t.parseFail}`); });
  if (!geos.length) throw new Error(`${file.name}: ${cfg.t.parseFail}`);
  const a = analyse(geos);
  if (!(a.vol > 0)) throw new Error(`${file.name}: ${cfg.t.parseFail}`);
  const maxDim = Math.max(a.size.x, a.size.y, a.size.z);
  const item = {
    uid: Math.random().toString(36).slice(2, 10), file, name: file.name, geos,
    vol: a.vol, area: a.area, size: a.size, openEdges: a.openEdges,
    unit: maxDim < 12 ? 25.4 : 1,                      // tiny extents → probably inches
    f: { x: 1, y: 1, z: 1 },
    ...defaults(), ...preset
  };
  items.push(item);
  return items.length - 1;
}

async function handleFiles(list) {
  showError('');
  const drop = $('[data-drop]');
  drop.style.opacity = '.5';
  const errors = [];
  let last = -1;
  for (const f of list) {
    try { last = await addFile(f); } catch (e) { errors.push(e.message); }
  }
  drop.style.opacity = '';
  if (errors.length) showError(errors.join(' · '));
  if (last >= 0) select(last); else render();
}

const fileInput = $('[data-file]');
const drop = $('[data-drop]');
// warm the library up on first intent, so parsing starts the moment the file lands
['pointerenter', 'focusin', 'dragenter', 'touchstart'].forEach(ev => drop.addEventListener(ev, loadThree, { once: true, passive: true }));
fileInput.addEventListener('change', () => { handleFiles([...fileInput.files]); fileInput.value = ''; });
['dragenter', 'dragover'].forEach(ev => drop.addEventListener(ev, (e) => { e.preventDefault(); drop.classList.add('is-over'); }));
['dragleave', 'drop'].forEach(ev => drop.addEventListener(ev, () => drop.classList.remove('is-over')));
drop.addEventListener('drop', (e) => { e.preventDefault(); handleFiles([...e.dataTransfer.files]); });
// once the workspace is open the drop zone is gone — accept drops anywhere on it
const work = $('[data-work]');
work.addEventListener('dragover', (e) => { if (items.length) e.preventDefault(); });
work.addEventListener('drop', (e) => { if (items.length && e.dataTransfer?.files?.length) { e.preventDefault(); handleFiles([...e.dataTransfer.files]); } });
$('[data-add-more]').addEventListener('click', () => fileInput.click());

$('[data-qty]')?.parentElement?.addEventListener('click', (e) => {
  const b = e.target.closest('[data-qty]');
  if (!b) return;
  e.stopPropagation();                        // app.js has a global stepper too — without this each click adds 2
  const input = $('[data-pieces]');
  input.value = Math.min(500, Math.max(1, (parseInt(input.value, 10) || 1) + (+b.dataset.qty)));
  input.dispatchEvent(new Event('input', { bubbles: true }));
});

$('[data-reset-view]').addEventListener('click', frameView);

/* Lay the part another way: turn the geometry itself 90° about the file's X axis, so the
   plate, dimensions and size inputs all follow. Volume doesn't change; Y and Z swap.
   Turns are saved with the line (_rot) so the print is set up the way the customer chose. */
function rotateModel(it) {
  const m = new THREE.Matrix4().makeRotationX(Math.PI / 2);
  it.geos.forEach(g => { g.applyMatrix4(m); g.computeBoundingBox(); g.computeVertexNormals(); });
  it.size = new THREE.Vector3(it.size.x, it.size.z, it.size.y);
  it.f = { x: it.f.x, y: it.f.z, z: it.f.y };
  it.rot = ((it.rot || 0) + 1) % 4;
}
$('[data-rotate]')?.addEventListener('click', () => {
  const it = items[active];
  if (!it) return;
  rotateModel(it);
  afterResize();
  hideHint();
});
$$('[data-zoom]').forEach(b => b.addEventListener('click', () => { zoomBy(b.dataset.zoom === 'in' ? 0.82 : 1.22); hideHint(); }));

$('[data-fullscreen]').addEventListener('click', () => {
  const el = $('[data-viewer]');
  if (document.fullscreenElement) document.exitFullscreen();
  else el.requestFullscreen?.().catch(() => {});
});

/* the hint says its piece once, then stays out of the way */
let hintSeen = false;
function hideHint() { const h = $('[data-hint]'); if (h) h.hidden = true; }
function showHint() {
  const h = $('[data-hint]');
  if (!h || hintSeen) return;
  hintSeen = true;
  h.hidden = false;
  setTimeout(hideHint, 5000);
}

$('[data-compare]').addEventListener('change', (e) => {
  compareKey = e.target.value;
  if (!meshGroup) return;
  placeRef();
  frameView();
});

/* size: type a dimension, nudge the percentage, or let it fill the plate */
const afterResize = () => { const it = items[active]; if (!it) return; showModel(it); syncSize(it); render(); };

$$('[data-dim]').forEach(el => el.addEventListener('change', () => {
  const it = items[active];
  if (!it) return;
  resizeTo(it, el.dataset.dim, parseFloat(el.value));
  afterResize();
}));

$('[data-pct]').addEventListener('change', (e) => {
  const it = items[active];
  if (!it) return;
  const next = Math.min(20, Math.max(0.01, (parseFloat(e.target.value) || 100) / 100));
  AXES.forEach(a => { it.f[a] = next; });
  afterResize();
});

$('[data-lock]').addEventListener('change', (e) => {
  const it = items[active];
  if (!it || !e.target.checked) return;
  AXES.forEach(a => { it.f[a] = it.f.x; });           // re-locking snaps back to one ratio
  afterResize();
});

/* a sample model, built here, so the tool can be tried without a file at hand */
$('[data-sample]').addEventListener('click', async () => {
  await loadThree();                                   // a quick tap can beat the lazy import
  const g = new THREE.TorusKnotGeometry(18, 6, 160, 24).toNonIndexed();
  const p = g.attributes.position.array;
  const tris = p.length / 9;
  const dv = new DataView(new ArrayBuffer(84 + tris * 50));
  dv.setUint32(80, tris, true);
  for (let i = 0; i < tris; i++) {
    let o = 84 + i * 50 + 12;
    for (let j = 0; j < 9; j++, o += 4) dv.setFloat32(o, p[i * 9 + j], true);
  }
  const file = new File([dv.buffer], 'sample.stl', { type: 'model/stl' });
  await handleFiles([file]);
});

$('[data-files]').addEventListener('click', (e) => {
  const rm = e.target.closest('[data-remove]');
  if (rm) {
    const i = +rm.dataset.remove;
    items.splice(i, 1);
    select(items.length ? Math.min(i, items.length - 1) : -1);
    return;
  }
  const pick = e.target.closest('[data-pick]');
  if (pick) select(+pick.dataset.pick);
});
$('[data-files]').addEventListener('keydown', (e) => {
  const pick = e.target.closest('[data-pick]');
  if (pick && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); select(+pick.dataset.pick); }
});

/* spec controls edit the selected file */
form.addEventListener('change', (e) => {
  const it = items[active];
  const t = e.target;
  if (!it) { renderSummary(); return; }
  if (t.name === '_material') it.material = t.value;
  else if (t.name === '_tier') { it.tier = t.value; it.opts = { ...TIERS[tierIdOf(t.value)] || TIERS.standard }; syncOptions(it); }
  else if (t.name === '_color') it.color = t.value;
  else if (t.matches('[data-pieces]')) it.pieces = Math.max(1, parseInt(t.value, 10) || 1);
  else if (t.dataset.opt) { setOption(it, t); }
  else return;
  render();
});
form.addEventListener('input', (e) => {
  const it = items[active];
  if (!it) return;
  if (e.target.matches('[data-pieces]')) { it.pieces = Math.max(1, parseInt(e.target.value, 10) || 1); render(); }
  if (e.target.matches('[data-opt="infill"]')) { setOption(it, e.target); render(); }
  if (e.target.matches('[data-note]')) it.note = e.target.value;
});
/* an advanced control changed: keep the tier the price is booked under (variant option 2)
   and the options that actually drive the price apart */
function setOption(it, el) {
  const next = { ...it.opts };
  if (el.dataset.opt === 'supports') next.supports = el.checked;
  else if (el.dataset.opt === 'infill') next.infill = parseInt(el.value, 10);
  else next[el.dataset.opt] = el.value;
  it.opts = normaliseOptions({ tier: tierIdOf(it.tier), ...next });
  syncOptions(it);
}

/* ================================================================ thumbnails
   The cart and checkout can only show the print service product's own image, so every line
   would otherwise be the same placeholder. A square crop of the preview travels with the
   file as a hidden property and the cart templates use it instead. */
const THUMB = 320;

/* Drawn into a render target we own rather than read back off the visible canvas — the
   drawing buffer is not readable in every browser, an offscreen target always is. */
function snapshot() {
  const rt = new THREE.WebGLRenderTarget(THUMB, THUMB);
  const aspect = camera.aspect;
  const savedPos = camera.position.clone(), savedTarget = controls.target.clone();
  camera.aspect = 1; camera.updateProjectionMatrix();
  /* frame the part alone, square, a little further back — the viewer's framing is
     for a wide canvas and the plate, and cropped to a tile it showed a corner */
  const box = new THREE.Box3().setFromObject(meshGroup);
  if (!box.isEmpty()) {
    const size = box.getSize(new THREE.Vector3()), mid = box.getCenter(new THREE.Vector3());
    const dist = Math.max(size.x, size.y, size.z) * 1.9;
    camera.position.set(mid.x + dist * .8, mid.y + dist * .6, mid.z + dist * .8);
    camera.lookAt(mid);
  }
  scene.background = new THREE.Color(0x6B717C);        // a black print needs a light ground to show against in the cart tile
  renderer.setRenderTarget(rt);
  renderer.render(scene, camera);
  const px = new Uint8Array(THUMB * THUMB * 4);
  renderer.readRenderTargetPixels(rt, 0, 0, THUMB, THUMB, px);
  renderer.setRenderTarget(null);
  scene.background = null;
  camera.position.copy(savedPos); controls.target.copy(savedTarget); camera.lookAt(savedTarget);
  camera.aspect = aspect; camera.updateProjectionMatrix();
  rt.dispose();

  // Some GL stacks hand back an empty buffer instead of the frame. A flat image is worse
  // than none, so say so and let the cart fall back to the product image.
  let varied = 0;
  for (let i = 4; i < px.length; i += 4) if (px[i] !== px[0] || px[i + 1] !== px[1] || px[i + 2] !== px[2]) varied++;
  if (varied < THUMB * THUMB * 0.01) return Promise.resolve(null);

  const c = document.createElement('canvas');
  c.width = c.height = THUMB;
  const row = THUMB * 4;
  const flipped = new Uint8ClampedArray(px.length);      // GL reads bottom-up
  for (let y = 0; y < THUMB; y++) flipped.set(px.subarray(y * row, y * row + row), (THUMB - 1 - y) * row);
  c.getContext('2d').putImageData(new ImageData(flipped, THUMB, THUMB), 0, 0);
  return new Promise((res) => c.toBlob(res, 'image/png'));
}

async function thumbnails() {
  const out = new Map();
  if (!renderer) return out;
  for (const it of items) { showModel(it); out.set(it.uid, await snapshot().catch(() => null)); }
  if (items[active]) showModel(items[active]);
  return out;
}

/* Server pricing must succeed before a cart mutation. */
async function sign(it,q) {
  if (!cfg.signUrl) throw new Error(S.t?.error || 'Quote service is not configured');
  const r=await fetch(cfg.signUrl,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({
    quoteId:it.uploadId,variantId:q.v.id,quantity:q.qty,pieces:it.pieces,volume:q.volMm3,area:q.areaMm2,height:q.heightMm,opts:packOptions(it.opts)
  })});
  const d=await r.json().catch(()=>({}));
  if (!r.ok || !d.sig || d.quantity!==q.qty) throw new Error(d.error || S.t?.error || 'Quote could not be verified');
  return d;
}
async function readCart() {
  const r=await fetch((S.routes.cart || '/cart')+'.js',{cache:'no-store'});
  if (!r.ok) throw new Error(S.t?.error || 'Cart unavailable');
  return r.json();
}

/* what the options are, in words, for the cart, checkout and the order */
const describeOptions = (o) => [
  `${cfg.t.optInfill} ${o.infill}%`,
  cfg.t.optWall[o.wallStrength],
  cfg.t.optLayer[o.quality],
  o.finish !== 'standard' ? cfg.t.optFinish[o.finish] : null,
  o.supports ? cfg.t.optSupports : null,
].filter(Boolean).join(' · ');

/* ================================================================ submit */
function upload(fd, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', S.routes.cartAdd + '.js');
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.upload.onprogress = (ev) => ev.lengthComputable && onProgress(ev.loaded);
    xhr.onload = () => {
      let d = {}; try { d = JSON.parse(xhr.responseText); } catch (_) {}
      if (xhr.status === 413) return reject(new Error(cfg.t.tooBig));
      if (xhr.status < 200 || xhr.status >= 300) return reject(new Error(d.description || d.message || S.t.error));
      resolve(d);
    };
    xhr.onerror = () => reject(new Error(S.t.error));
    xhr.send(fd);
  });
}

form.addEventListener('submit', async (e) => {
  e.preventDefault();
  if (!items.length || submitting) return;
  submitting=true;
  const btn = $('[data-submit]');
  const label = btn.innerHTML;
  const bar = $('[data-upload]'), fill = $('[data-upload-fill]'), txt = $('[data-upload-text]');
  btn.disabled = true; btn.innerHTML = '<span class="spin"></span>';
  bar.hidden = false; fill.style.width = '0%';
  const totalBytes = items.reduce((s, it) => s + it.file.size, 0) || 1;
  let doneBytes = 0;
  const started = Date.now();
  const group = Math.random().toString(36).slice(2, 8);

  try {
    const staged=new Map();
    for (const it of items) {
      it.uploadId ||= crypto.randomUUID();
      const q=quoteFor(it); staged.set(it.uid,{q,signature:await sign(it,q)});
    }
    const thumbs = await thumbnails();
    for (const it of items) {
      const {q,signature}=staged.get(it.uid);
      // Covers a lost upload response as well as a known partial success.
      const existing=(await readCart()).items.find(l=>l.properties?._upload_id===it.uploadId);
      if (existing) {
        if (existing.variant_id!==q.v.id || existing.quantity!==q.qty || existing.properties._opts!==packOptions(it.opts) || +existing.properties._pieces!==it.pieces) throw new Error(cfg.t.alreadyAdded || 'This file is in your cart. Remove it there before changing its specifications.');
        doneBytes+=it.file.size;continue;
      }
      const fd = new FormData();
      fd.append('properties[_upload_id]',it.uploadId);
      fd.append('properties[_pieces]',it.pieces);
      fd.append('id', q.v.id);
      fd.append('quantity', q.qty);
      fd.append(`properties[${cfg.props.file}]`, it.file, it.file.name);
      fd.append(`properties[${cfg.props.specs}]`, `${it.material} · ${it.tier}`);
      fd.append(`properties[${cfg.props.options}]`, describeOptions(it.opts));
      if (it.color) fd.append(`properties[${cfg.props.color}]`, it.color);
      fd.append(`properties[${cfg.props.pieces}]`, it.pieces);
      fd.append(`properties[${cfg.props.dims}]`, q.dims);
      if (it.note.trim()) fd.append(`properties[${cfg.props.note}]`, it.note.trim());
      fd.append('properties[_grams]', q.grams);
      fd.append('properties[_volume_mm3]', q.volMm3);
      fd.append('properties[_area_mm2]', q.areaMm2);
      fd.append('properties[_height_mm]', q.heightMm);
      fd.append('properties[_opts]', packOptions(it.opts));
      fd.append('properties[_unit]', it.unit);
      fd.append('properties[_fit]', `${it.f.x},${it.f.y},${it.f.z}`);
      if (it.rot) fd.append('properties[_rot]', `x${it.rot * 90}`);
      fd.append('properties[_group]', group);
      fd.append('properties[_name]', it.name);
      fd.append(`properties[${cfg.props.name}]`, it.name);   // checkout can only show properties
      const thumb = thumbs.get(it.uid);
      if (thumb) fd.append('properties[_thumb]', thumb, 'preview.png');
      fd.append('properties[_sig]',signature.sig);
      fd.append('properties[_sig_v]',signature.version);
      await upload(fd, (loaded) => {
        const p = (doneBytes + loaded) / totalBytes;
        const secs = (Date.now() - started) / 1000;
        const eta = p > .03 ? Math.max(0, Math.round(secs / p - secs)) : null;
        fill.style.width = `${Math.round(p * 100)}%`;
        txt.textContent = `${cfg.t.uploading} ${Math.round(p * 100)}%${eta !== null && p < 1 ? ` · ~${eta}s` : ''}${items.length > 1 ? ` · ${items.indexOf(it) + 1}/${items.length}` : ''}`;
      });
      doneBytes += it.file.size;
    }
    if (editKey) {
      const r=await fetch(S.routes.cartChange+'.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({id:editKey,quantity:0})});
      if (!r.ok) throw new Error(S.t?.error || 'Replacement added; original could not be removed. Retry to finish.');
      if ((await readCart()).items.some(l=>l.key===editKey)) throw new Error(S.t?.error || 'Original line still exists');
    }
    txt.textContent = '✓';
    btn.innerHTML = label;
    if (editKey) { history.replaceState({}, '', location.pathname); editKey = null; document.querySelector('[data-edit-note]').hidden = true; }
    items.splice(0, items.length); select(-1);
    setTimeout(() => { bar.hidden = true; }, 1200);
    if (window.refreshCartUI) await window.refreshCartUI({ open: true }); else location.href = S.routes.cart;
  } catch (err) {
    showError(err.message || S.t?.error);
    bar.hidden = true;
    btn.innerHTML = label;
    renderSummary();
  } finally {submitting=false;renderSummary();}
});

/* ================================================================ edit from cart */
async function loadEdit(key) {
  try {
    const cart = await fetch((S.routes.cart || '/cart') + '.js').then(r => r.json());
    const line = cart.items.find(i => Object.values(i.properties || {}).some(v => typeof v === 'string' && v.includes('/uploads/' + key)));
    if (!line) throw new Error('missing');
    const props = line.properties || {};
    const url = Object.values(props).find(v => typeof v === 'string' && v.includes('/uploads/'));
    if (!url) throw new Error('no file');
    const blob = await fetch(url).then(r => { if (!r.ok) throw new Error('download'); return r.blob(); });
    const name = props._name || decodeURIComponent(url.split('/').pop().split('?')[0]);
    const v = cfg.variants.find(x => x.id === line.variant_id) || {};
    const preset = {
      material: v.o1, tier: v.o2,
      opts: unpackOptions(props._opts, tierIdOf(v.o2)),
      color: props[cfg.props.color] || defaults().color,
      pieces: parseInt(props[cfg.props.pieces], 10) || 1,
      note: props[cfg.props.note] || ''
    };
    const idx = await addFile(new File([blob], name), preset);
    const turns = parseInt(String(props._rot || '').replace('x', ''), 10) / 90 || 0;
    for (let t = 0; t < turns; t++) rotateModel(items[idx]);  // before _fit, which was saved in the turned axes
    if (props._unit) items[idx].unit = +props._unit;
    else if (props._scale) items[idx].unit = +props._scale;          // lines saved before per-axis sizing
    if (props._fit) {
      const parts = String(props._fit).split(',').map(Number);
      if (parts.length === 3 && parts.every(n => n > 0)) items[idx].f = { x: parts[0], y: parts[1], z: parts[2] };
    }
    editKey = line.key;
    document.querySelector('[data-edit-note]').hidden = false;
    select(idx);
  } catch (e) {
    showError(cfg.t.editFail);
  }
}

const params = new URLSearchParams(location.search);
if (params.get('edit')) loadEdit(params.get('edit'));
render();
